import {
  IDocModule,
  IDocUnitLesson,
  IEducationalAlignment,
  IModuleParams,
  IResourceParams,
  ParsedGoogleDocType,
} from './types'
import {
  addUnitsToCourse,
  createActivity,
  createLongCardListType,
  createNoteNode,
} from '../nodeCreationHelpers'
import { mapSubject } from '../utility'
import {
  ICourseNode,
  INoteNode,
  ISimplifiedNode,
  IUnitNode,
} from '../../components/ingestion/IngestedTree'
import { NodeType } from 'shared/types/ingestion'
import {
  getGradeCode,
  performanceExpectations,
  skillsListFromAlignments,
} from './utility'
import { parseUnitLesson } from './unit_lesson'
import {
  createUnorderedList,
  PARAGRAPH_SPACING,
  wrapB,
  wrapP,
  wrapTag,
} from '../html_utility'
import {
  createMaterialAttachment,
  createTeacherReferenceMaterialsSection,
} from './teacherReferenceMaterials'
import { getUnitCourseName, getUnitDayTotal } from './moduleUtility'
import { lessonSets } from './lessonSets'

export async function parseUnitModules(sortedParams: IResourceParams[]) {
  const metadata = sortedParams[0].metadata as IDocModule
  const subject = await mapSubject(metadata.forCourse.courseIdentifier)
  const gradeId = getGradeCode(metadata.educationalAlignment)
  const units = sortedParams.map((params) => {
    const moduleParams: IModuleParams = {
      ...params,
      metadata: params.metadata as IDocModule,
      gradeId,
      subject,
      unitCode: params.metadata.courseCode.split('/')[0],
    }
    return createUnit(moduleParams)
  })
  return { gradeId, subject, units }
}

export function createCourse(
  { gradeId, subjectId }: { gradeId: number; subjectId: number },
  units: IUnitNode[],
): ICourseNode {
  const course: ICourseNode = {
    type: NodeType.Course,
    data: {
      subjectID: subjectId,
      gradeID: gradeId,
      title: `OpenSciEd Grade ${gradeId - 1} | FL Edition: Powered by Kiddom`,
      attribution: 'OSE',
    },
    nodes: [],
  }
  addUnitsToCourse(course, units)
  return course
}

function createUnit(params: IModuleParams): IUnitNode {
  const title = `Unit ${params.unitCode} ${params.metadata.about}`
  const unit: IUnitNode = {
    type: NodeType.Unit,
    data: {
      title,
    },
    nodes: unitNodes(params),
  }
  return unit
}

function unitNodes(params: IModuleParams): ISimplifiedNode[] {
  return [
    ...unitSkillsList(params),
    unitOverview(params),
    ...lessonSets(params),
    ...getUnitStoryline(params),
    createTeacherReferenceMaterialsSection(params),
    ...getLessonPlans(params),
  ]
}

function unitOverview(params: IModuleParams): INoteNode {
  const boldPart = wrapP(wrapB(params.htmlContent.title))
  const parts = params.metadata.description.split('\r\n')
  let index = 0
  const parsedParts: string[] = [boldPart]
  while (index < parts.length) {
    const part = parts[index]
    switch (true) {
      case part.startsWith('\t'): {
        // beginning of ul element
        const items: string[] = []
        while (index < parts.length && parts[index].startsWith('\t')) {
          items.push(parts[index] + '<br><br>')
          index++
        }
        // step back to last table item
        index--
        parsedParts.push(createUnorderedList(items))
        break
      }
      case part.endsWith(':'):
        // bold item
        parsedParts.push(wrapP(wrapB(part)))
        break
      case part.includes(':'): {
        const boldIndex = part.indexOf(':')
        const boldPart = part.slice(0, boldIndex + 1)
        parsedParts.push(wrapP(wrapB(boldPart) + part.slice(boldIndex + 1)))
        break
      }
      default:
        part.trim().length && parsedParts.push(wrapP(part))
    }
    index++
  }

  parsedParts.push(
    performanceExpectations(params.metadata.educationalAlignment),
  )

  return createNoteNode({
    title: 'Unit Overview',
    value: parsedParts.join(PARAGRAPH_SPACING),
    studentFacing: false,
  })
}

function getUnitStoryline(params: IModuleParams) {
  const nodes: ISimplifiedNode[] = [
    createNoteNode({
      title: `Unit Storyline - ${getUnitDayTotal(params)} Days Total`,
      value: wrapB(
        wrapTag('span', getUnitCourseName(params), {
          style: 'font-size: 24px;',
        }),
      ),
      studentFacing: false,
    }),
  ]
  const material = params.metadata.material.find(
    (m) => m['@id'] === 'unit-storyline',
  )
  if (material) {
    nodes.push(createMaterialAttachment(material))
  }
  return nodes
}

function gDriveDocForLesson(
  gDrive: Record<string, ParsedGoogleDocType>,
  lesson: IDocUnitLesson,
  messageCallback: (cb: (m: string[]) => string[]) => void,
): ParsedGoogleDocType | undefined {
  const [, , coursePart, , lessonPart] = lesson['@id'].split('-')
  const lessonKey = coursePart + lessonPart + 'TG'
  if (lessonKey in gDrive) {
    return gDrive[lessonKey]
  }
  messageCallback((prev) => {
    return [
      ...prev,
      `Missing Lesson Doc: expected to find with key "${lessonKey}", coursePart: ${coursePart}; lessonPart: ${lessonPart}; + TG`,
    ]
  })
}

function getLessonPlans(params: IModuleParams) {
  const { lessonPlans, studentGuides } = params.metadata.hasPart.reduce<{
    lessonPlans: ISimplifiedNode[]
    studentGuides: ISimplifiedNode[]
  }>(
    ({ lessonPlans, studentGuides }, part) => {
      part.hasPart.forEach((lessonPart) => {
        const lessonDoc = gDriveDocForLesson(
          params.gDrive,
          lessonPart,
          params.messageCallback,
        )
        if (lessonDoc) {
          const { lessonPlan, studentGuides: newStudentGuides } =
            parseUnitLesson(lessonPart, lessonDoc, params)
          lessonPlans.push(lessonPlan)
          studentGuides.push(...newStudentGuides)
        }
      })
      return {
        lessonPlans,
        studentGuides,
      }
    },
    { lessonPlans: [], studentGuides: [] },
  )

  return [
    createLongCardListType({
      title: 'Science Notebook',
      nodes: [
        createActivity(
          {
            title: `${params.unitCode} Science Notebook`,
          },
          studentGuides,
        ),
      ],
    }),
    createLongCardListType({
      title: 'Lesson Plans',
      nodes: lessonPlans,
    }),
  ]
}

function unitSkillsList(params: IModuleParams) {
  const skillsList = skillsListFromAlignments(getLessonAlignments(params))
  if (!skillsList) return []
  return [skillsList]
}

function getLessonAlignments(params: IModuleParams) {
  return params.metadata.hasPart.reduce<IEducationalAlignment[]>(
    (acc, part) => {
      part.hasPart.forEach(({ educationalAlignment }) =>
        acc.push(...educationalAlignment),
      )
      return acc
    },
    [],
  )
}
